import React, { useState, useEffect, useMemo } from 'react';
import {
  Calendar,
  Filter,
  Download,
  Plus,
  Users,
  User,
  MapPin,
  Clock,
  Phone,
  Mail,
  ChevronDown,
  ChevronUp,
  Search,
  ArrowRight
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * Module Planning par intervenants actifs - Version Premium
 */
const PlanningIntervenantsModule = ({
  db,
  employees,
  clients,
  appointments,
  roles,
  currentUser,
  onAddAppointment
}) => {
  const [filters, setFilters] = useState({
    dateRange: 'today',
    role: 'all'
  });
  const [showFilters, setShowFilters] = useState(false);
  const [expandedIntervenants, setExpandedIntervenants] = useState(new Set());
  const [intervenantsStats, setIntervenantsStats] = useState({});

  // Filtrer les intervenants
  const activeIntervenants = useMemo(() => {
    return employees.filter(emp => {
      if (emp.role === 'beneficiaire') return false;
      const isActive = emp.isActive !== false && !emp.isArchived;
      const matchesRole = filters.role === 'all' || emp.role === filters.role;
      return isActive && matchesRole;
    });
  }, [employees, filters.role]);

  // Calcul des stats (inchangé dans la logique, juste optimisé)
  useEffect(() => {
    const stats = {};
    const now = new Date();
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - now.getDay() + 1);
    startOfWeek.setHours(0, 0, 0, 0);
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);
    endOfWeek.setHours(23, 59, 59, 999);

    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    endOfMonth.setHours(23, 59, 59, 999);

    activeIntervenants.forEach(intervenant => {
      const intervenantAppointments = appointments.filter(apt => apt.employeeId === intervenant.id);

      let filteredAppointments = intervenantAppointments.filter(apt => {
        const aptDate = apt.date?.toDate ? apt.date.toDate() : new Date(apt.date);
        switch (filters.dateRange) {
          case 'today': return aptDate.toDateString() === today.toDateString();
          case 'week': return aptDate >= startOfWeek && aptDate <= endOfWeek;
          case 'month': return aptDate >= startOfMonth && aptDate <= endOfMonth;
          default: return true;
        }
      });

      filteredAppointments.sort((a, b) => {
        const dateA = a.date?.toDate ? a.date.toDate() : new Date(a.date);
        const dateB = b.date?.toDate ? b.date.toDate() : new Date(b.date);
        if (dateA.getTime() !== dateB.getTime()) return dateA.getTime() - dateB.getTime();
        const [hA, mA] = a.startTime.split(':').map(Number);
        const [hB, mB] = b.startTime.split(':').map(Number);
        return (hA * 60 + mA) - (hB * 60 + mB);
      });

      const currentAppointments = intervenantAppointments.filter(apt => {
        const aptDate = apt.date?.toDate ? apt.date.toDate() : new Date(apt.date);
        const [sH, sM] = apt.startTime.split(':').map(Number);
        const [eH, eM] = apt.endTime.split(':').map(Number);
        const start = new Date(aptDate); start.setHours(sH, sM, 0);
        const end = new Date(aptDate); end.setHours(eH, eM, 0);
        return start <= now && end >= now;
      });

      const totalHours = filteredAppointments.reduce((acc, apt) => {
        if (!apt.startTime || !apt.endTime) return acc;
        const [sH, sM] = apt.startTime.split(':').map(Number);
        const [eH, eM] = apt.endTime.split(':').map(Number);
        return acc + ((eH * 60 + eM) - (sH * 60 + sM)) / 60;
      }, 0);

      stats[intervenant.id] = {
        totalAppointments: filteredAppointments.length,
        currentAppointments: currentAppointments.length,
        totalHours: Math.round(totalHours * 10) / 10,
        appointments: filteredAppointments
      };
    });
    setIntervenantsStats(stats);
  }, [activeIntervenants, appointments, filters.dateRange]);

  const toggleIntervenant = (id) => {
    setExpandedIntervenants(prev => {
      const newSet = new Set(prev);
      newSet.has(id) ? newSet.delete(id) : newSet.add(id);
      return newSet;
    });
  };

  const getClientName = (id) => {
    const client = clients.find(c => c.id === id);
    return client ? `${client.firstName} ${client.lastName}` : 'Inconnu';
  };

  const getRoleName = (id) => roles.find(r => r.id === id)?.name || id;

  const formatDateRange = () => {
    const now = new Date();
    if (filters.dateRange === 'today') return `Aujourd'hui - ${now.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}`;
    if (filters.dateRange === 'week') return "Cette semaine";
    if (filters.dateRange === 'month') return now.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
    return 'Toutes périodes';
  };

  // Stats globales pour le header
  const globalStats = {
    totalIntervenants: activeIntervenants.length,
    totalRDV: Object.values(intervenantsStats).reduce((s, st) => s + st.totalAppointments, 0),
    totalHeures: Object.values(intervenantsStats).reduce((s, st) => s + st.totalHours, 0),
    avecRDV: Object.values(intervenantsStats).filter(st => st.totalAppointments > 0).length
  };

  // Composant Carte Stat (Interne)
  const StatCard = ({ label, value, subLabel, icon: Icon, color }) => (
    <div className={`relative overflow-hidden rounded-2xl p-5 border border-gray-200 dark:border-slate-700/50 backdrop-blur-md ${color === 'blue' ? 'bg-gradient-to-br from-blue-50 to-white dark:from-blue-900/20 dark:to-slate-900/60' :
        color === 'emerald' ? 'bg-gradient-to-br from-emerald-50 to-white dark:from-emerald-900/20 dark:to-slate-900/60' :
          color === 'amber' ? 'bg-gradient-to-br from-amber-50 to-white dark:from-amber-900/20 dark:to-slate-900/60' :
            'bg-gradient-to-br from-purple-50 to-white dark:from-purple-900/20 dark:to-slate-900/60'
      }`}>
      <div className="flex items-center gap-4 relative z-10">
        <div className={`p-3 rounded-xl ${color === 'blue' ? 'bg-blue-500/20 text-blue-400' :
            color === 'emerald' ? 'bg-emerald-500/20 text-emerald-400' :
              color === 'amber' ? 'bg-amber-500/20 text-amber-400' :
                'bg-purple-500/20 text-purple-400'
          }`}>
          <Icon size={24} />
        </div>
        <div>
          <h3 className="text-2xl font-bold text-gray-900 dark:text-white">{value}</h3>
          <p className="text-gray-500 dark:text-slate-400 text-sm">{label}</p>
          {subLabel && <p className={`text-xs mt-1 ${color === 'blue' ? 'text-blue-400' :
              color === 'emerald' ? 'text-emerald-400' :
                color === 'amber' ? 'text-amber-400' :
                  'text-purple-400'
            }`}>{subLabel}</p>}
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900 text-gray-900 dark:text-slate-100 p-6 font-sans">
      <div className="max-w-7xl mx-auto space-y-8">

        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-400">
              Planning par Intervenants
            </h1>
            <p className="text-gray-500 dark:text-slate-400 mt-1">Vue consolidée des activités</p>
          </div>

          <div className="flex gap-3">
            <button
              onClick={() => onAddAppointment && onAddAppointment()}
              className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-500 text-white rounded-xl shadow-lg shadow-blue-500/20 transition-all font-medium"
            >
              <Plus size={18} />
              <span>Nouveau RDV</span>
            </button>
            <button className="flex items-center gap-2 px-5 py-2.5 bg-gray-100 dark:bg-slate-800 hover:bg-gray-200 dark:hover:bg-slate-700 text-gray-700 dark:text-slate-300 border border-gray-200 dark:border-slate-700 rounded-xl transition-all">
              <Download size={18} />
              <span>Exporter</span>
            </button>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <StatCard
            label="Intervenants Actifs"
            value={globalStats.totalIntervenants}
            subLabel={`Dont ${globalStats.avecRDV} planifiés`}
            icon={Users}
            color="blue"
          />
          <StatCard
            label="RDV Planifiés"
            value={globalStats.totalRDV}
            subLabel={filters.dateRange === 'today' ? "Aujourd'hui" : "Sur la période"}
            icon={Calendar}
            color="emerald"
          />
          <StatCard
            label="Heures Planifiées"
            value={Math.round(globalStats.totalHeures)}
            subLabel="Total heures"
            icon={Clock}
            color="amber"
          />
          <StatCard
            label="Taux d'Assignation"
            value={`${globalStats.totalIntervenants > 0 ? Math.round((globalStats.avecRDV / globalStats.totalIntervenants) * 100) : 0}%`}
            subLabel="Intervenants occupés"
            icon={User}
            color="purple"
          />
        </div>

        {/* Filters & Période */}
        <div className="flex flex-col md:flex-row justify-between items-center gap-4 bg-white/80 dark:bg-slate-800/40 backdrop-blur-sm p-4 rounded-xl border border-gray-200 dark:border-slate-700/50">
          <div className="flex items-center gap-4">
            <div className="bg-gray-100 dark:bg-slate-700/50 px-4 py-2 rounded-lg border border-gray-200 dark:border-slate-600">
              <span className="text-blue-500 dark:text-blue-400 font-semibold">{formatDateRange()}</span>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-all ${showFilters
                  ? 'bg-blue-500/20 border-blue-500/50 text-blue-400'
                  : 'bg-gray-100 dark:bg-slate-800 border-gray-200 dark:border-slate-700 text-gray-500 dark:text-slate-400 hover:bg-gray-200 dark:hover:bg-slate-750'
                }`}
            >
              <Filter size={18} />
              <span>Filtres</span>
            </button>
          </div>
        </div>

        {/* Expanded Filters */}
        <AnimatePresence>
          {showFilters && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-white/80 dark:bg-slate-800/40 p-6 rounded-xl border border-gray-200 dark:border-slate-700/50 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-500 dark:text-slate-400 mb-2">Période</label>
                  <select
                    value={filters.dateRange}
                    onChange={(e) => setFilters(p => ({ ...p, dateRange: e.target.value }))}
                    className="w-full bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-lg px-4 py-2.5 text-gray-800 dark:text-slate-200 outline-none focus:border-blue-500 transition-colors"
                  >
                    <option value="today">Aujourd'hui</option>
                    <option value="week">Cette semaine</option>
                    <option value="month">Ce mois</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500 dark:text-slate-400 mb-2">Rôle</label>
                  <select
                    value={filters.role}
                    onChange={(e) => setFilters(p => ({ ...p, role: e.target.value }))}
                    className="w-full bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-lg px-4 py-2.5 text-gray-800 dark:text-slate-200 outline-none focus:border-blue-500 transition-colors"
                  >
                    <option value="all">Tous les rôles</option>
                    {roles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                  </select>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* List of Intervenants */}
        <div className="space-y-4">
          <AnimatePresence>
            {activeIntervenants.map((intervenant, index) => {
              const stats = intervenantsStats[intervenant.id] || { totalAppointments: 0, totalHours: 0, appointments: [], currentAppointments: 0 };
              const isExpanded = expandedIntervenants.has(intervenant.id);

              return (
                <motion.div
                  key={intervenant.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white/80 dark:bg-slate-800/40 backdrop-blur-md rounded-xl border border-gray-200 dark:border-slate-700/50 overflow-hidden hover:border-gray-300 dark:hover:border-slate-600 transition-colors"
                >
                  <div
                    onClick={() => toggleIntervenant(intervenant.id)}
                    className="p-5 cursor-pointer flex flex-col md:flex-row items-center justify-between gap-4"
                  >
                    {/* Info Intervenant */}
                    <div className="flex items-center gap-5 w-full md:w-auto">
                      <div className="relative">
                        <div className="w-14 h-14 rounded-full bg-gray-200 dark:bg-slate-700 flex items-center justify-center text-xl font-bold text-gray-600 dark:text-slate-300 overflow-hidden border-2 border-gray-300 dark:border-slate-600">
                          {intervenant.photoURL ? (
                            <img src={intervenant.photoURL} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <span>{intervenant.firstName?.[0]}{intervenant.lastName?.[0]}</span>
                          )}
                        </div>
                        {stats.currentAppointments > 0 && (
                          <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-emerald-500 rounded-full border-2 border-white dark:border-slate-800 animate-pulse"></div>
                        )}
                      </div>

                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{intervenant.firstName} {intervenant.lastName}</h3>
                        <p className="text-sm text-gray-500 dark:text-slate-400 flex items-center gap-2">
                          <span className="px-2 py-0.5 bg-gray-100 dark:bg-slate-700/50 rounded text-xs">{getRoleName(intervenant.role)}</span>
                          {intervenant.phoneNumber && (
                            <span className="flex items-center gap-1 text-xs"><Phone size={12} />{intervenant.phoneNumber}</span>
                          )}
                        </p>
                      </div>
                    </div>

                    {/* Stats Compactes */}
                    <div className="flex items-center gap-8 w-full md:w-auto justify-between md:justify-end">
                      <div className="text-center md:text-right">
                        <p className="text-gray-400 dark:text-slate-400 text-xs uppercase tracking-wider">RDV</p>
                        <p className="text-xl font-bold text-blue-400">{stats.totalAppointments}</p>
                      </div>
                      <div className="text-center md:text-right">
                        <p className="text-gray-400 dark:text-slate-400 text-xs uppercase tracking-wider">Heures</p>
                        <p className="text-xl font-bold text-emerald-400">{stats.totalHours}h</p>
                      </div>
                      <div className={`p-2 rounded-full transition-transform duration-300 ${isExpanded ? 'rotate-180 bg-gray-200 dark:bg-slate-700' : 'bg-gray-100 dark:bg-slate-800'}`}>
                        <ChevronDown size={20} className="text-gray-500 dark:text-slate-400" />
                      </div>
                    </div>
                  </div>

                  {/* Accordion Content */}
                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="border-t border-gray-200 dark:border-slate-700/50 bg-gray-50 dark:bg-slate-900/30"
                      >
                        <div className="p-4 md:p-6 space-y-4">
                          {stats.appointments.length > 0 ? (
                            <div className="relative border-l-2 border-gray-200 dark:border-slate-700 ml-4 space-y-6">
                              {stats.appointments.map((apt, i) => {
                                const aptDate = apt.date?.toDate ? apt.date.toDate() : new Date(apt.date);
                                const now = new Date();
                                const [sH, sM] = apt.startTime?.split(':').map(Number) || [0, 0];
                                const [eH, eM] = apt.endTime?.split(':').map(Number) || [0, 0];
                                const start = new Date(aptDate); start.setHours(sH, sM);
                                const end = new Date(aptDate); end.setHours(eH, eM);
                                const isCurrent = start <= now && end >= now;
                                const isUpcoming = start > now;

                                return (
                                  <div key={i} className="relative pl-6">
                                    <div className={`absolute -left-[9px] top-1 w-4 h-4 rounded-full border-2 border-white dark:border-slate-900 ${isCurrent ? 'bg-emerald-500 animate-pulse' :
                                        isUpcoming ? 'bg-blue-500' : 'bg-gray-400 dark:bg-slate-500'
                                      }`}></div>

                                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-gray-50 dark:bg-slate-800/40 p-4 rounded-lg border border-gray-200 dark:border-slate-700/30 hover:bg-gray-100 dark:hover:bg-slate-800/60 transition-colors">
                                      <div>
                                        <div className="flex items-center gap-2 mb-1">
                                          <span className={`text-xs font-bold px-2 py-0.5 rounded ${isCurrent ? 'bg-emerald-500/20 text-emerald-400' :
                                              isUpcoming ? 'bg-blue-500/20 text-blue-400' :
                                                'bg-gray-200 dark:bg-slate-700 text-gray-500 dark:text-slate-400'
                                            }`}>
                                            {isCurrent ? 'EN COURS' : isUpcoming ? 'À VENIR' : 'TERMINÉ'}
                                          </span>
                                          <span className="text-gray-500 dark:text-slate-400 text-sm">{apt.startTime} - {apt.endTime}</span>
                                        </div>
                                        <h4 className="text-gray-900 dark:text-white font-medium text-lg">{getClientName(apt.clientId)}</h4>
                                        <p className="text-gray-400 dark:text-slate-500 text-sm flex items-center gap-2 mt-1">
                                          <MapPin size={14} />
                                          {clients.find(c => c.id === apt.clientId)?.address?.city || 'Adresse non renseignée'}
                                        </p>
                                      </div>

                                      {apt.description && (
                                        <div className="text-sm text-gray-500 dark:text-slate-400 italic border-l-2 border-gray-200 dark:border-slate-700 pl-3 md:max-w-xs">
                                          "{apt.description}"
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <div className="text-center py-8 text-gray-400 dark:text-slate-500">
                              <Calendar className="mx-auto mb-2 opacity-50" size={32} />
                              <p>Aucun rendez-vous sur cette période</p>
                            </div>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </AnimatePresence>

          {activeIntervenants.length === 0 && (
            <div className="text-center py-12 bg-gray-50 dark:bg-slate-800/30 rounded-xl border border-gray-200 dark:border-slate-700 border-dashed">
              <Users className="mx-auto text-gray-400 dark:text-slate-600 mb-4" size={48} />
              <h3 className="text-xl font-bold text-gray-500 dark:text-slate-400">Aucun intervenant trouvé</h3>
              <p className="text-gray-400 dark:text-slate-500">Essayez de modifier vos filtres</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PlanningIntervenantsModule;
